// ============================================================
// MOCK DATA for the frontend-first build.
// Everything here is placeholder. Once the real tenant account
// and the real PM-owner account are provided, these get replaced
// by API calls (see notes in each portal component).
// ============================================================

// ---- Property Management lifecycle (Owner) ----
export type LifecycleStatus = 'done' | 'active' | 'pending';

export interface LifecycleStep {
  key: string;
  label: string;
  description: string;
  status: LifecycleStatus;
  date?: string;
}

// The SAME unit the portfolio, the PM tab and the tenant portal all show.
// This preview used to run on a different invented property with its own rent
// and its own dates, so the app contradicted itself depending on which screen
// you opened.
export const OWNER_UNIT = {
  id: 'demo-unit-seagate',
  name: '1204 • Seagate Building 2',
  community: 'Mina Rashid',
  bedrooms: '2 Bedrooms',
  size: '1,412 Sqft',
  price: 'AED 3,150,000',
  status: 'Rented',
  image: 'https://i.imgur.com/Wq09VFd.jpeg',
};

export const LIFECYCLE_STEPS: LifecycleStep[] = [
  { key: 'snagging', label: 'Snagging', description: 'Defect inspection completed, 12 items logged and fixed.', status: 'done', date: '18 Jan 2023' },
  { key: 'handover', label: 'Handover', description: 'Keys received, DEWA activated.', status: 'done', date: '14 Feb 2023' },
  { key: 'placement', label: 'Tenant Placement', description: 'Tenant placed at AED 198,000 a year.', status: 'done', date: '08 Jan 2026' },
  { key: 'contracts', label: 'Contracts', description: 'Tenancy contract signed, Ejari EJ-2026-118427 registered.', status: 'done', date: '16 Jan 2026' },
  { key: 'rental', label: 'Rental Tracking', description: 'Four cheques a year, collected and paid out.', status: 'active' },
];

// ---- Rent confirmation flow (Owner approves incoming payments) ----
// 'paid' is what the PM admin writes when a cheque clears (the status
// dropdown offers Scheduled / Awaiting approval / Approved / Paid), so the
// union has to carry it — every `status === 'paid'` check in the owner view
// was otherwise comparing against a type that could not hold it.
export type RentStatus = 'awaiting_approval' | 'approved' | 'scheduled' | 'paid';

export interface RentPayment {
  id: string;
  chequeNo: string;
  amount: number;
  dueDate: string;
  status: RentStatus;
  method: string;
}

// ---- Complaints (Tenant submits -> internal team only, owner NOT notified) ----
export type ComplaintStatus = 'submitted' | 'in_review' | 'resolved';

export interface Complaint {
  id: string;
  category: string;
  title: string;
  detail: string;
  status: ComplaintStatus;
  createdAt: string;
}

export const TENANT_COMPLAINTS: Complaint[] = [
  { id: 'c1', category: 'Plumbing', title: 'Kitchen sink leaking', detail: 'Water pooling under the sink cabinet.', status: 'in_review', createdAt: '28 Aug 2026' },
  { id: 'c2', category: 'AC', title: 'AC not cooling in bedroom', detail: 'Bedroom AC blows warm air after 20 min.', status: 'submitted', createdAt: '04 Sep 2026' },
];

export const COMPLAINT_CATEGORIES = ['Plumbing', 'Electrical', 'AC / Cooling', 'Appliances', 'Pest Control', 'General Maintenance', 'Other'];

// ---- Tenant profile / overview ----
//
// This is the OTHER SIDE of a tenancy the showcase already tells from the
// owner's chair: Mounir Haddad rents Seagate Building 2 · 1204, the unit that
// appears in the Owner view's portfolio and in its Property Management tab.
// Name, rent, contract dates, Ejari number and the four cheques all match
// demoPmData.ts — so the Owner and Tenant films can be cut as one story, and
// a viewer who watches both never catches the two sides disagreeing.
//
// Keep the dates ahead of today when refreshing this: an expired contract and
// an overdue cheque are what a stale fixture looks like on camera.
/**
 * THE TENANCY CALENDAR — one source of truth for both sides of this lease.
 *
 * Four quarterly cheques across the contract year. Which of them have been
 * banked is DERIVED from today rather than written down: a fixture that still
 * says the July cheque is "with the owner" in September reads as a stalled
 * process, and this tenancy gets filmed. The owner's Property Management view
 * builds its rent schedule from this same list (see demoPmData), so the two
 * sides cannot drift apart.
 *
 * The contract year itself is still fixed. Past Jan 2027 this needs rolling
 * forward — as does the rest of the sample portfolio.
 */
const CHEQUE_AMOUNT = 49500;
const CHEQUE_DATES = ['2026-01-15', '2026-04-15', '2026-07-15', '2026-10-15'];

const startOfToday = (): number => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime(); };

export interface TenancyCheque {
  chequeNo: string; amount: number; iso: string; dueDate: string; paid: boolean;
  status: 'paid' | 'awaiting_approval' | 'scheduled';
}

const chequeBase = CHEQUE_DATES.map((iso, i) => ({
  chequeNo: `CHQ-104${i + 1}`,
  amount: CHEQUE_AMOUNT,
  iso,
  dueDate: new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
  paid: new Date(iso).getTime() < startOfToday(),
}));

/**
 * Statuses, and why they fall where they do:
 *   past due   -> paid. A cheque whose date has gone by has cleared; showing
 *                 one still "awaiting approval" months later says the managing
 *                 agent sat on it, which is the opposite of the pitch.
 *   next one   -> awaiting_approval. The tenant hands the next cheque over
 *                 early — that is how quarterly cheques work, they are all
 *                 written at signing — so it sits with the owner to approve
 *                 BEFORE its due date. The tenant sees "with the owner" for
 *                 the same cheque. Both sides are true at once.
 *   after that -> scheduled.
 */
export const TENANCY_CHEQUES: TenancyCheque[] = (() => {
  const firstUnpaid = chequeBase.findIndex((c) => !c.paid);
  return chequeBase.map((c, i) => ({
    ...c,
    status: c.paid ? 'paid' as const : i === firstUnpaid ? 'awaiting_approval' as const : 'scheduled' as const,
  }));
})();

// Built from the tenancy calendar above, so the owner's cheque list, the PM
// tab's rent schedule and the tenant's payment screen are the same four
// cheques in the same states — and none of them can age into a cheque that is
// "awaiting approval" long after its due date.
export const RENT_PAYMENTS: RentPayment[] = TENANCY_CHEQUES.map((c, i) => ({
  id: `r${i + 1}`,
  chequeNo: c.chequeNo,
  amount: c.amount,
  dueDate: c.dueDate,
  status: c.status as RentStatus,
  method: 'Cheque',
}));

const chequesPaid = TENANCY_CHEQUES.filter((c) => c.paid);
const nextCheque = TENANCY_CHEQUES.find((c) => !c.paid);

export const TENANT = {
  id: 'tenant-001',
  name: 'Mounir Haddad',
  unit: '1204 • Seagate Building 2',
  community: 'Mina Rashid',
  contractStart: '15 Jan 2026',
  contractEnd: '14 Jan 2027',
  annualRent: CHEQUE_AMOUNT * CHEQUE_DATES.length,
  paidToDate: chequesPaid.reduce((sum, c) => sum + c.amount, 0),
  installments: TENANCY_CHEQUES.length,
  installmentsPaid: chequesPaid.length,
  nextPaymentAmount: nextCheque ? nextCheque.amount : 0,
  nextPaymentDate: nextCheque ? nextCheque.dueDate : '',
  ejariNo: 'EJ-2026-118427',
  // The same project photo the owner's card uses, so the tenant's home and
  // the owner's asset are visibly one building.
  image: 'https://i.imgur.com/Wq09VFd.jpeg',
};

/**
 * The tenant's cheques, in the shape the portal renders. Same numbers, dates
 * and states as the owner's rentSchedule, because both come from
 * TENANCY_CHEQUES above.
 */
export const TENANT_RENT: { chequeNo: string; amount: number; dueDate: string; paid: boolean; status: string }[] =
  TENANCY_CHEQUES.map((c) => ({
    chequeNo: c.chequeNo,
    amount: c.amount,
    dueDate: c.dueDate,
    paid: c.paid,
    status: c.status,
  }));

export interface TenantDoc {
  id: string;
  name: string;
  type: string;
  date: string;
}

export const TENANT_DOCS: TenantDoc[] = [
  { id: 'd1', name: 'Tenancy Contract 2026-2027', type: 'Contract', date: '15 Jan 2026' },
  { id: 'd2', name: 'Ejari Certificate · EJ-2026-118427', type: 'Ejari', date: '19 Jan 2026' },
  { id: 'd3', name: 'Move-in Inventory List', type: 'Inventory', date: '15 Jan 2026' },
  { id: 'd4', name: 'DEWA Registration', type: 'Utility', date: '17 Jan 2026' },
];

// ---- EA (staff) customer list ----
export interface EACustomer {
  id: string;
  name: string;
  email: string;
  units: number;
  role: 'owner' | 'tenant';
  pmActive: boolean;
}

export const EA_CUSTOMERS: EACustomer[] = [
  { id: 'inv-001', name: 'Max Mustermann', email: 'max@example.com', units: 3, role: 'owner', pmActive: true },
  { id: 'inv-002', name: 'Sarah Weber', email: 'sarah@example.com', units: 1, role: 'owner', pmActive: false },
  { id: 'tenant-001', name: 'Ahmed Khan', email: 'ahmed@example.com', units: 1, role: 'tenant', pmActive: true },
  { id: 'inv-003', name: 'Liu Chen', email: 'liu@example.com', units: 2, role: 'owner', pmActive: true },
];

export const EA_UPLOAD_TYPES = ['Tenancy Contract', 'Inventory List', 'Security Cheque', 'Rent Cheque', 'Ejari Certificate', 'Handover Document'];

// Documents already on file per customer (keyed by EACustomer.id)
export const EA_CUSTOMER_UPLOAD: Record<string, { type: string; name: string }[]> = {
  'inv-001': [
    { type: 'Tenancy Contract', name: 'Tenancy Contract 2024-2025.pdf' },
    { type: 'Inventory List', name: 'Move-in Inventory — 1611 The Ivy.pdf' },
    { type: 'Rent Cheque', name: 'CHQ-001 · AED 20,000.jpg' },
  ],
  'tenant-001': [
    { type: 'Ejari Certificate', name: 'Ejari Certificate.pdf' },
    { type: 'Security Cheque', name: 'Security Cheque · AED 5,000.jpg' },
  ],
  'inv-003': [
    { type: 'Handover Document', name: 'Handover — The Vybe 1704.pdf' },
  ],
};
